import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { SpanishAcknowledgmentService } from 'projects/borrower-app/src/app/services/spanish-acknowledgment.service';
import { UiLanguageService } from 'projects/borrower-app/src/app/services/ui-language.service';

@Component({
  selector: 'language-switcher',
  templateUrl: './language-switcher.component.html',
  styles: [`
    :host {
      display: inline-flex;
      align-items: center;
    }
    .lang-switcher {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      padding: 3px 6px;
      border-radius: 999px;
      background: transparent;
      transition: background-color 0.15s ease;
      font-size: 12px;
      letter-spacing: 0.04em;
      line-height: 1;
    }
    .lang-switcher:hover {
      background: rgba(0, 0, 0, 0.035);
    }
    .lang-icon {
      font-size: 12px;
      color: #9ca3af;
      line-height: 1;
      margin-right: 0;
    }
    .lang-btn {
      position: relative;
      appearance: none;
      border: 0;
      background: transparent;
      padding: 3px 4px;
      font: inherit;
      font-weight: 500;
      color: #9ca3af;
      cursor: pointer;
      text-transform: uppercase;
      letter-spacing: inherit;
      transition: color 0.15s ease;
      line-height: 1;
    }
    .lang-btn:hover:not(.active) {
      color: #4b5563;
    }
    .lang-btn.active {
      color: #0d6efd;
      font-weight: 700;
      background: rgba(13, 110, 253, 0.08);
      border-radius: 4px;
      cursor: default;
    }
    .lang-btn:focus-visible {
      outline: 2px solid #3b82f6;
      outline-offset: 2px;
      border-radius: 4px;
    }
    .lang-sep {
      color: #d1d5db;
      user-select: none;
      line-height: 1;
    }
  `],
  standalone: false
})
export class LanguageSwitcherComponent implements OnInit, OnDestroy {

  // Backend-resolved notice shown when switching to the non-default language; '' = none.
  // Auth-agnostic widget: parent owns visibility (*ngIf) and supplies this. Must not read
  // ApplicationContext (doing so leaked authenticated calls onto public pages — ADO 24613).
  @Input() notice: string = '';

  activeLang: string;

  readonly languages = [
    { code: 'en', label: 'EN' },
    { code: 'es', label: 'ES' }
  ];

  private _langSubscription: Subscription | undefined;

  // UiLanguageService holds the application context itself, so this widget stays auth-agnostic:
  // on public pages (no application) it makes no calls.
  constructor(private readonly translocoService: TranslocoService,
    private readonly _uiLanguage: UiLanguageService,
    private readonly _spanishAcknowledgment: SpanishAcknowledgmentService) {}

  ngOnInit() {
    this.activeLang = this.translocoService.getActiveLang();
    // The saved language can be applied after this widget renders (LLS-886) — keep the highlight in step.
    this._langSubscription = this.translocoService.langChanges$.subscribe(lang => this.activeLang = lang);
  }

  ngOnDestroy() {
    this._langSubscription?.unsubscribe();
  }

  async switchLang(lang: string): Promise<void> {
    // LLS-886 §5: signed out, the lender's Spanish acknowledgment is asked before switching (declined = stay).
    if (lang === 'es' && !(await this._spanishAcknowledgment.confirmSwitchToSpanish())) {
      return;
    }
    this.translocoService.setActiveLang(lang);
    this.activeLang = lang;
    localStorage.setItem('preferred-lang', lang);
    this._uiLanguage.recordUserChoice(lang);

    // The acknowledgment, when the lender has it on, replaces this informational notice.
    if (lang === 'es' && this.notice && !(await this._spanishAcknowledgment.isEnabled())) {
      const escaped = this.notice
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');

      Swal.fire({
        html: escaped,
        icon: 'info',
        confirmButtonText: 'OK',
        width: 'auto',
        customClass: { popup: 'swal-language-notice-popup', htmlContainer: 'swal-language-notice' }
      });
    }
  }
}
